import { useSelector } from 'react-redux'
import { ttsSelectors } from 'store/features/tts/selectors'
import { ttsThunks } from 'store/features/tts/thunks'
import { TRootState } from 'store/types'

import { useMemo, useEffect } from 'react'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { dispatch } from 'store'
import { useAlertPopupsContext } from 'context/AlertPopups'
import { TTS_LANGUAGE_MISMATCH_ERROR_IMG } from 'consts/images'
import { useGetTtsItemSpeakerData } from 'containers/SubSidebar/shared/Voices/hooks'

type TSelectorFactory<T> = () => (state: TRootState, id: number) => T

const useGenericTtsItemSelector = <T>(
  selectorFactory: TSelectorFactory<T>,
  id: number
): T => {
  const memoizedSelector = useMemo(() => selectorFactory(), [selectorFactory])
  const value = useSelector((state: TRootState) => memoizedSelector(state, id))
  return value
}

//this one is to get the loading state from timeline tts audio, thus need to pass the assetId that is a stringified number
export const useGetTimelineTtsAudioLoadingState = (
  audioAssetId: number | string
) =>
  useGenericTtsItemSelector(
    ttsSelectors.makeTtsItemLoadingStateById,
    Number(audioAssetId)
  )
//to get the generated state when clikcing on audiotts item in timeline by the assetid from tts items (asset id is id in ttsitems)
export const useGetTimelineTtsAudioGeneratedState = (
  audioAssetId: number | string
) =>
  useGenericTtsItemSelector(
    ttsSelectors.makeTtsItemGeneratedStateById,
    Number(audioAssetId)
  )

export const useGetTtsItemLoadingState = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeTtsItemLoadingStateById, id)

export const useGetTtsItemGeneratedState = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeTtsItemGeneratedStateById, id)

export const useGetTtsItemTextValue = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeTtsItemTextValueById, id)

export const useGetTtsItemDraftState = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeTtsItemDraftStateById, id)

export const useGetInitialTtsItemText = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeInitialTtsItemTextById, id)

export const useGetInitialTtsItemSpeakerId = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeInitialTtsItemSpeakerIdById, id)

export const useGetInitialTtsItemLangCode = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeInitialTtsItemLangCodeById, id)

export const useGetInitialTtsItemInstruction = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeInitialTtsItemInstructionById, id)

export const useGetTtsItemSpeakerId = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeTtsItemSpeakerIdById, id)

export const useGetTtsItemLangCode = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeTtsItemLangCodeById, id)

export const useGetTtsItemSpeed = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeTtsItemSpeedById, id)

export const useGetTtsItemInstruction = (id: number) =>
  useGenericTtsItemSelector(ttsSelectors.makeTtsItemInstructionById, id)

export const useGetTtsItemSpeakerProps = (id: number) => {
  const speakerId = useGetTtsItemSpeakerId(id)
  const speed = useGetTtsItemSpeed(id)
  const langCode = useGetTtsItemLangCode(id)
  const instructions = useGetTtsItemInstruction(id)
  const speakerData = useGetTtsItemSpeakerData(speakerId, langCode)

  return {
    displayName: speakerData?.displayName || '',
    hasEmotion: speakerData?.hasEmotion || false,
    isPremium: speakerData?.isPremium || false,
    speakerId,
    speed,
    langCode,
    instructions
  }
}

export const useGetTtsTimelineId = (id: number) => {
  const ttsAudios = useSelector(projectDataSelectors.ttsAudios)
  const ttsAudio = ttsAudios.find(audio => audio.assetId === id.toString())
  return ttsAudio?.id || null
}

export const useGetTtsMismatchPopupVisible = () => {
  const isTtsMismatchPopupVisible = useSelector(
    ttsSelectors.isTtsMismatchPopupVisible
  )

  const { setAlertPopupsData } = useAlertPopupsContext()

  useEffect(() => {
    if (isTtsMismatchPopupVisible) {
      setAlertPopupsData({
        warningPopup: {
          title: translate(T.GENERATION_FAILED),
          description: translate(T.TTS_GENERATION_ERROR_DESCRIPTION),
          imageSrc: TTS_LANGUAGE_MISMATCH_ERROR_IMG,
          buttons: {
            confirm: {
              label: translate(T.GOT_IT_LABEL),
              onClick: () => {
                dispatch(ttsThunks.setIsTtsMismatchPopupVisible(false))
              }
            }
          },
          onClose: () => {
            dispatch(ttsThunks.setIsTtsMismatchPopupVisible(false))
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTtsMismatchPopupVisible])
}
